import { useEffect, useMemo, useState } from 'react';

import { corePdpLink } from 'components/helpers/links';
import type { HubBuyingTour } from 'components/hub/useHubBuyerListingsAndTours';
import {
  buildPropertyPhotoUrl,
  buildPropertySpecsLine,
} from 'components/marketplace/property/helpers';
import { formatCurrency } from 'helpers/formatters';

export interface HubTour {
  id: string;
  listingId: string | null;
  addressId: string;
  addressLine: string;
  priceLabel: string | null;
  detailsLine: string | null;
  photoUrl: string | null;
  href: string;
  timeRange: string;
  dateLabel: string;
  isPast: boolean;
  // Mirrors iOS: cancel is offered only before the tour starts. Legacy rows that are not
  // Trippy-backed cannot be canceled through buyerTourCancel, which always cancels in Trippy.
  canCancel: boolean;
}

const MAX_PAST_TOURS = 10;
// setTimeout overflows above this delay and fires immediately.
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;

const formatTime = (date: Date, timeZone?: string | null): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  })
    .format(date)
    .toLowerCase();

// e.g. "Jun 15, 2026"
const formatAbsoluteDate = (date: Date, timeZone?: string | null): string =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);

export const buildTimeRange = (start: Date, end: Date | null, timeZone?: string | null): string => {
  if (end && !Number.isNaN(end.getTime())) {
    return `${formatTime(start, timeZone)} - ${formatTime(end, timeZone)}`;
  }
  return formatTime(start, timeZone);
};

export const buildDateLabel = (start: Date, now: Date, timeZone?: string | null): string => {
  const isToday = formatAbsoluteDate(start, timeZone) === formatAbsoluteDate(now, timeZone);
  return isToday ? 'Today' : formatAbsoluteDate(start, timeZone);
};

interface ParsedTour {
  tour: HubBuyingTour;
  addressId: string;
  start: Date;
  end: Date | null;
  isPast: boolean;
}

export const parseTour = (tour: HubBuyingTour, now: number): ParsedTour | null => {
  if (!tour.scheduledFor || !tour.addressId) {
    return null;
  }
  const start = new Date(String(tour.scheduledFor));
  if (Number.isNaN(start.getTime())) {
    return null;
  }
  const end = tour.endTime ? new Date(String(tour.endTime)) : null;
  const finishMs = end && !Number.isNaN(end.getTime()) ? end.getTime() : start.getTime();
  return { tour, addressId: tour.addressId, start, end, isPast: finishMs < now };
};

export const buildHubTour = (
  { tour, addressId, start, end, isPast }: ParsedTour,
  now: Date,
): HubTour => {
  const base: HubTour = {
    id: tour.id,
    listingId: tour.listingId,
    addressId,
    addressLine: tour.readableAddress ?? 'Home tour',
    priceLabel: null,
    detailsLine: null,
    photoUrl: null,
    href: corePdpLink('_', addressId),
    timeRange: buildTimeRange(start, end, tour.timezone),
    dateLabel: buildDateLabel(start, now, tour.timezone),
    isPast,
    canCancel: tour.usingTrippy === true && start.getTime() > now.getTime(),
  };

  const { listing } = tour;
  if (!listing) {
    return base;
  }

  return {
    ...base,
    addressLine: listing.street ?? base.addressLine,
    priceLabel: listing.listPriceCents != null ? formatCurrency(listing.listPriceCents) : null,
    detailsLine: buildPropertySpecsLine(
      listing.bedrooms,
      listing.bathrooms,
      listing.totalLivingSquareFootage,
    ),
    photoUrl: buildPropertyPhotoUrl(listing.photoUrls[0], { preset: '3:2-md' }),
    href: corePdpLink(listing.slug ?? '_', listing.addressId),
  };
};

export function useHomeTours(
  rawTours: HubBuyingTour[],
  isBuyerDataLoading: boolean,
): {
  upcoming: HubTour[];
  past: HubTour[];
  isLoading: boolean;
} {
  const [boundaryReachedAtMs, setBoundaryReachedAtMs] = useState(0);
  const { upcoming, past, nextBoundaryMs } = useMemo(() => {
    // Which tours have passed, and the relative labels built from `now`, are only correct as of
    // the moment this recomputes -- freezing it at mount leaves a started tour in `upcoming`.
    // No hook expresses "re-read an impure value when these deps change": mount-time state
    // freezes it, and an effect trades this for set-state-in-effect.
    // oxlint-disable-next-line react/purity
    const nowMs = Math.max(Date.now(), boundaryReachedAtMs);
    const now = new Date(nowMs);

    const parsed = rawTours
      .map((tour) => parseTour(tour, nowMs))
      .filter((parsedTour): parsedTour is ParsedTour => parsedTour !== null);

    const upcomingTours = parsed
      .filter((parsedTour) => !parsedTour.isPast)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .map((parsedTour) => buildHubTour(parsedTour, now));
    const pastTours = parsed
      .filter((parsedTour) => parsedTour.isPast)
      .sort((a, b) => b.start.getTime() - a.start.getTime())
      .slice(0, MAX_PAST_TOURS)
      .map((parsedTour) => buildHubTour(parsedTour, now));

    // The next moment an upcoming tour starts (cancel closes) or ends (it becomes past).
    const boundaries = parsed
      .filter((parsedTour) => !parsedTour.isPast)
      .map(({ start, end }) =>
        start.getTime() > nowMs ? start.getTime() : (end?.getTime() ?? start.getTime()),
      )
      .filter((boundaryMs) => boundaryMs > nowMs);
    const nextBoundary = boundaries.length > 0 ? Math.min(...boundaries) : null;

    return { upcoming: upcomingTours, past: pastTours, nextBoundaryMs: nextBoundary };
  }, [rawTours, boundaryReachedAtMs]);

  useEffect(
    function recomputeAtNextTourBoundary() {
      if (nextBoundaryMs === null) {
        return undefined;
      }
      const delayMs = nextBoundaryMs - Date.now();
      if (delayMs > MAX_TIMER_DELAY_MS) {
        return undefined;
      }
      const timer = setTimeout(
        // +1 so the recompute lands strictly past the boundary: isPast and the next-boundary
        // filter both use strict comparisons.
        () => setBoundaryReachedAtMs(Math.max(Date.now(), nextBoundaryMs + 1)),
        Math.max(delayMs, 0),
      );
      return () => clearTimeout(timer);
    },
    [nextBoundaryMs],
  );

  return { upcoming, past, isLoading: isBuyerDataLoading };
}
